import Grid from '@material-ui/core/Grid';
import { makeStyles } from '@material-ui/core/styles';
import PropTypes from 'prop-types';
import React from 'react';
import { useMediaQueryDown } from '../../../../../../hooks/useMediaQuery';
import { useTrans } from '../../../../../../services/i18n';
import { formatRelativeTime } from '../../../../../../utils/dateUtils';
import { Chip } from '../../../../../common/Chip';
import { Paper } from '../../../../../common/Containers';
import { SpacingContainer } from '../../../../../common/Containers/Base';
import { CheckBox } from '../../../../../common/Inputs';
import Typography from '../../../../../common/Typography';
import {
  getActionByItem,
  getButtonAriaLabel,
  getButtonLabelKey,
  getChipBGByItemType,
  getChipColorByItemType,
  getChipLabelByItemType,
  getDescriptionByItem,
  getDescriptionColorByItemType,
  getSubtitleByItem,
  getTitleByItem,
  hasSubtitleByItemType,
  isCrmAlertItem,
} from '../helpers';
import { useHistory } from 'react-router-dom';
import { Button } from '../../../../../common/Buttons';

const useStyles = makeStyles((theme) => ({
  visuallyHidden: {
    position: 'absolute',
    width: 1,
    height: 1,
    padding: 0,
    margin: -1,
    overflow: 'hidden',
    clip: 'rect(0 0 0 0)',
    whiteSpace: 'nowrap',
    border: 0,
  },
  title: {
    maxWidth: '100%',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  card: {
    borderRadius: 10,
    boxShadow: '0 1px 3px rgba(16, 24, 40, 0.1), 0 1px 2px rgba(16, 24, 40, 0.06)',
    paddingTop: theme.spacing(1.5),
    paddingBottom: theme.spacing(1.5),
  },
  chip: {
    height: 24,
    borderRadius: 4,
    '& .MuiChip-label': {
      paddingLeft: theme.spacing(1.5),
      paddingRight: theme.spacing(1.5),
      fontWeight: 500,
    },
  },
  actionButton: {
    height: 34,
    padding: theme.spacing(0, 1.75),
    backgroundColor: theme.palette.common.white,
    color: '#2F3949',
    border: '1px solid #E1E4E8',
    borderRadius: 6,
    fontSize: '0.875rem',
    fontWeight: 500,
    zIndex: 1,
  },
  subtitle: {
    maxWidth: '100%',
    fontSize: '0.875rem',
    color: theme.palette.gray.main,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  crmSubtitle: {
    maxWidth: '100%',
    fontSize: '0.875rem',
    color: theme.palette.gray.main,
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
  },
  centered: {
    display: 'flex',
    alignItems: 'center',
  },
}));

const getTitleColor = (chosen, isUnread) => {
  if (chosen) return 'white';
  return isUnread ? 'primary' : 'label';
};

const openNotification = ({ item, target, history, onMarkAsRead }) => {
  if (!target) return;
  if (isCrmAlertItem(item) && !item.read && onMarkAsRead) onMarkAsRead({ id: item.id });
  history.push(target);
};

function CardTitle({ item, chosen, styles, trans }) {
  const isUnread = !item.read;
  const subtitle = hasSubtitleByItemType(item.itemType) ? getSubtitleByItem(item, trans) : '';

  return (
    <Grid
      item
      container
      xs={12}
      sm={12}
      md={6}
      justifyContent="center"
      alignItems="flex-start"
      style={{ display: 'flex', flexDirection: 'column' }}
    >
      <Typography
        variant="body1"
        color={getTitleColor(chosen, isUnread)}
        weight={isUnread ? '600' : '400'}
        className={styles.title}
      >
        {isUnread && <span className={styles.visuallyHidden}>{trans('unread')} </span>}
        {getTitleByItem(item)}
      </Typography>
      {subtitle && (
        <Typography
          fontFamily="manrope"
          weight="400"
          className={isCrmAlertItem(item) ? styles.crmSubtitle : styles.subtitle}
        >
          {subtitle}
        </Typography>
      )}
    </Grid>
  );
}

function CardActionButton({ item, onOpen, trans, className }) {
  return (
    <Button
      onClick={(e) => {
        e.stopPropagation();
        onOpen();
      }}
      aria-label={getButtonAriaLabel(item, trans)}
      size="small"
      className={className}
      style={{ display: item.itemType ? 'block' : 'none' }}
    >
      {trans(getButtonLabelKey(item))}
    </Button>
  );
}

export default function NotificationSmallCard({
  item,
  onClick,
  onCheckItem,
  onMarkAsRead,
  now,
  checked = false,
  chosen = false,
  openSelected = false,
  ...rest
}) {
  const { trans } = useTrans();
  const styles = useStyles();
  const isSmScreen = useMediaQueryDown('sm');
  const history = useHistory();

  if (!item) return null;

  const dense = openSelected || isSmScreen;
  const openItem = () =>
    openNotification({ item, target: item.details?.linkTo || getActionByItem(item), history, onMarkAsRead });
  const openAction = () => openNotification({ item, target: getActionByItem(item), history, onMarkAsRead });

  return (
    <Paper component={SpacingContainer} px={2} bgColorHover={chosen ? 'primary' : 'active'} className={styles.card}>
      <Grid container alignItems={dense ? 'flex-start' : 'center'} spacing={2} wrap="nowrap">
        <Grid item xs="auto">
          <CheckBox
            checked={checked}
            onChange={onCheckItem ? onCheckItem.bind(null, item) : undefined}
            inputProps={{ 'aria-label': trans('select-alert', { title: getTitleByItem(item) }) }}
            dense
            color="secondary"
          />
        </Grid>

        <Grid onClick={openItem} className="pointer" item xs zeroMinWidth {...rest}>
          <Grid container spacing={dense ? 1 : 2}>
            <CardTitle item={item} chosen={chosen} styles={styles} trans={trans} />

            <Grid item xs={dense ? 12 : true} container justifyContent="flex-end" alignItems="center">
              <Typography
                variant="caption"
                weight="600"
                noWrap
                style={{ color: chosen ? 'gray' : getDescriptionColorByItemType(item.itemType) }}
              >
                {getDescriptionByItem(item)}
              </Typography>
            </Grid>

            <Grid item xs="auto" className={styles.centered}>
              <Typography variant="caption" color={chosen ? 'white' : 'hint'} noWrap>
                {formatRelativeTime(item.itemLastUpdated ?? item.createdAt, trans, now)}
              </Typography>
            </Grid>

            <Grid item xs="auto" className={styles.centered}>
              <Chip
                className={styles.chip}
                style={{
                  backgroundColor: getChipBGByItemType(item.itemType),
                  color: getChipColorByItemType(item.itemType),
                }}
                label={getChipLabelByItemType(item.itemType) || trans('not-provided')}
                size={isSmScreen ? 'small' : 'medium'}
              />
            </Grid>

            <Grid item xs="auto" className={styles.centered}>
              <CardActionButton item={item} onOpen={openAction} trans={trans} className={styles.actionButton} />
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </Paper>
  );
}

const itemShape = PropTypes.shape({
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  read: PropTypes.bool,
  itemType: PropTypes.string,
  itemLastUpdated: PropTypes.string,
  createdAt: PropTypes.string,
  details: PropTypes.shape({
    linkTo: PropTypes.string,
    message: PropTypes.string,
    contactName: PropTypes.string,
    count: PropTypes.number,
    action: PropTypes.shape({
      labelKey: PropTypes.string,
      nav: PropTypes.object,
    }),
  }),
});

CardTitle.propTypes = {
  item: itemShape.isRequired,
  chosen: PropTypes.bool,
  styles: PropTypes.object.isRequired,
  trans: PropTypes.func.isRequired,
};

CardActionButton.propTypes = {
  item: itemShape.isRequired,
  onOpen: PropTypes.func.isRequired,
  trans: PropTypes.func.isRequired,
  className: PropTypes.string,
};

NotificationSmallCard.propTypes = {
  item: itemShape,
  onClick: PropTypes.func,
  onCheckItem: PropTypes.func,
  onMarkAsRead: PropTypes.func,
  now: PropTypes.oneOfType([PropTypes.number, PropTypes.string, PropTypes.object]),
  checked: PropTypes.bool,
  chosen: PropTypes.bool,
  openSelected: PropTypes.bool,
};
